import { skipField } from '@/backend/entity'
import { toast } from '@/shared/toast'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import type { Field } from './Fields/types'
import { useProjectStore } from './useProjectStore'

export const useFieldSkip = () => {
  const projectStore = useProjectStore()
  const queryClient = useQueryClient()

  const skipFieldMutation = useMutation({
    mutationFn: async (field: Field) => {
      const result = await skipField(
        field.workspaceId,
        field.projectId,
        field.entityId,
        field.propertyId,
      )

      if (!result.ok) {
        throw new Error('Failed to skip field')
      }

      return result.data
    },
    onSuccess: (data, field) => {
      // Update the field in the project store with the new status
      projectStore.updateField({
        ...field,
        status: 'skip',
        staleness: data.staleness,
        updatedAt: data.updated_at,
      })

      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: ['entities', projectStore.projectId],
      })

      toast.success('Field skipped successfully')
    },
    onError: () => {
      toast.error('Failed to skip field. Please try again.')
    },
  })

  const skipFieldAction = (field: Field) => {
    if (field.status !== 'idle') {
      toast.error('Only idle fields can be skipped')
      return
    }

    skipFieldMutation.mutate(field)
  }

  return {
    skipField: skipFieldAction,
    isSkipping: skipFieldMutation.isPending,
  }
}
